import {
  Button,
  Skeleton,
  SkeletonButton,
  SkeletonText,
  Tooltip,
} from '@dev-spendesk/grapes';
import { useState } from 'react';

import { TokenInput } from 'common/components/TokenInput';
import { useTranslation } from 'common/hooks/useTranslation';
import { useCompany } from 'modules/app/hooks/useCompany';
import { useNotifications } from 'modules/app/notifications';
import { companyAPI } from 'src/core/api/axios';
import { QueryError } from 'src/core/common/components/QueryError';
import { QuerySuspense } from 'src/core/common/components/QuerySuspense';
import appConfig from 'src/core/config';
import { useUser } from 'src/core/modules/app/hooks/useUser';
import { formatCardExpirationDate } from 'src/core/modules/cards/models/formatCardExpirationDate';
import { MemberScreeningStepModal } from 'src/core/modules/members/components/MemberScreeningStepModal/MemberScreeningStepModal';
import {
  type UseScreeningInformationStatusQueryReponse,
  useScreeningInformationStatusQuery,
} from 'src/core/modules/members/hooks/useScreeningInformationStatusQuery';
import { SFSWhiteCardBackDesign } from 'src/core/modules/physical-cards/components/SFSCardDesign/SFSWhiteCardBackDesign';

import cardMaskSVG from './card-mask.svg';
import { type ApiCard } from '../../../../card';
import { CardDesign } from '../../../../components/CardDesign';
import { SFSCardBackDesign } from '../../../../components/SFSCardDesign/SFSCardBackDesign';

type Props = {
  card: ApiCard;
  goToNextStep: () => void;
  onFinish: () => void;
  screeningInformationStatus: UseScreeningInformationStatusQueryReponse['status'];
};

const Step2Body = ({
  card,
  goToNextStep,
  onFinish,
  screeningInformationStatus,
}: Props) => {
  const { t } = useTranslation('global');
  const { dangerNotif } = useNotifications();

  const company = useCompany();

  const [isActivating, setIsActivating] = useState(false);
  const [digitsValue, setDigitsValue] = useState(['', '', '', '']);
  const [isScreeningModalOpen, setIsScreeningModalOpen] = useState(false);

  const activate = async () => {
    if (
      card.banking_provider === 'sfs' &&
      screeningInformationStatus === 'pending'
    ) {
      setIsScreeningModalOpen(true);
      return;
    }

    setIsActivating(true);
    try {
      await activateCard({
        card,
        companyId: company.id,
        lastNumbers: digitsValue.join(''),
      });
      if (card.banking_provider === 'sfs') {
        onFinish();
      } else {
        goToNextStep();
      }
    } catch {
      dangerNotif(t('misc.somethingWrong'));
    } finally {
      setIsActivating(false);
    }
  };

  const isValid = () => {
    return (
      digitsValue.length === 4 &&
      digitsValue.every((value) => value && value !== '')
    );
  };

  const has4FirstDigits =
    card.card_visible_number &&
    card.banking_provider !== 'sfs' &&
    card.card_visible_number.length === 4 &&
    !Number.isNaN(Number(card.card_visible_number));
  const first4Digits = has4FirstDigits ? card.card_visible_number : '....';

  const cardNumbers = `${first4Digits} .... .... ${digitsValue
    .map((v) => v || '.')
    .join('')}`;

  return (
    <div className="w-[560px]">
      <h1 className="text-content-primary title-xxl mb-24">
        {t('cards.activation.step2.activateYourCardTitle')}
      </h1>
      <div className="box flex flex-col items-center gap-24">
        <div className="relative">
          {card.type !== 'physical' && (
            <img
              alt="card-digits"
              className="absolute top-0 right-0 bottom-0 left-0 z-[2]"
              src={cardMaskSVG}
            />
          )}
          {card.banking_provider === 'sfs' ? (
            <>
              {company.currency === 'GBP' &&
              card.design_type === 'FAST_CARD' ? (
                <SFSWhiteCardBackDesign
                  width={400}
                  numbers={cardNumbers}
                  name={card.card_name}
                />
              ) : (
                <SFSCardBackDesign
                  width={400}
                  numbers={cardNumbers}
                  name={card.card_name}
                />
              )}
            </>
          ) : (
            <CardDesign
              width={400}
              name={card.card_name}
              brand={appConfig.environmentName === 'demo' ? 'visa' : card.brand}
              numbers={cardNumbers}
              isActivating
              expDate={formatCardExpirationDate(card.card_exp_date)}
            />
          )}
        </div>

        <form
          id="card-digits"
          className="w-full"
          onSubmit={(event) => event.preventDefault()}
        >
          <h2 className="text-content-primary title-m">
            {t('cards.activation.step2.enterDigits')}
          </h2>
          <TokenInput
            token={digitsValue}
            tokenLength={4}
            onTokenChange={setDigitsValue}
          />
        </form>

        <Tooltip
          content={!isValid() ? t('cards.fillDigits') : undefined}
          triggerAsChild
        >
          <Button
            onClick={activate}
            isDisabled={!isValid()}
            type="submit"
            form="card-digits"
            text={
              isActivating
                ? t('cards.activation.step2.activateLoading')
                : t('cards.activation.step2.activateCta')
            }
          />
        </Tooltip>
      </div>
      <MemberScreeningStepModal
        isOpen={isScreeningModalOpen}
        onClose={() => setIsScreeningModalOpen(false)}
        onContinue={async () => {
          setIsScreeningModalOpen(false);
          setIsActivating(true);
          try {
            await activateCard({
              card,
              companyId: company.id,
              lastNumbers: digitsValue.join(''),
            });
            if (card.banking_provider === 'sfs') {
              onFinish();
            } else {
              goToNextStep();
            }
          } catch {
            dangerNotif(t('misc.somethingWrong'));
          } finally {
            setIsActivating(false);
          }
        }}
      />
    </div>
  );
};

const activateCard = async ({
  card,
  companyId,
  lastNumbers,
}: {
  card: ApiCard;
  companyId: string;
  lastNumbers: string;
}) => {
  if (card.status === 'BLO') {
    return companyAPI.put(
      `/cards/${card.id}/unpause`,
      {},
      {
        companyId,
      },
    );
  }
  return companyAPI.put(
    `/cards/${card.id}/activate`,
    { lastNumbers },
    {
      companyId,
    },
  );
};

export const Step2 = (props: Omit<Props, 'screeningInformationStatus'>) => {
  const user = useUser();

  const screeningInformationStatusQueryState =
    useScreeningInformationStatusQuery({
      userId: user.id,
    });

  return (
    <QuerySuspense
      queryState={screeningInformationStatusQueryState}
      fallback={(error) => (
        <QueryError
          queryError={error}
          componentType="Callout"
          logContext={{
            team: 'cards-ux',
          }}
        />
      )}
      loading={
        <div className="w-[560px]">
          <SkeletonText size="xxl" width="400px" className="mb-24" />
          <div className="box flex flex-col items-center gap-24">
            <Skeleton height="250px" width="400px" />
            <div className="w-full">
              <SkeletonText size="m" width="200px" className="mb-8" />
              <Skeleton height="48px" width="100%" />
            </div>
            <SkeletonButton />
          </div>
        </div>
      }
    >
      {({ status }) => (
        <Step2Body {...props} screeningInformationStatus={status} />
      )}
    </QuerySuspense>
  );
};
